import {
  extractDocument,
  loadPdfJs,
  resolveScale,
  buildArtefacts,
  glyphSourceFromFont,
  outlineDocument,
  type DocumentOutlines,
  type GlyphSource,
  ExtractionError,
  validateDocument,
  validateFailure,
  type ValidationReport,
  readDxf,
  verifyIdentity,
  toDrawingSpace,
  ROUND_TRIP_TOLERANCE_MM,
  ROUND_TRIP_ANGLE_TOLERANCE_DEG,
  type IdentityReport,
  computeMetrics,
  preprocess,
  baselineDetector,
  toEntities,
  mergeRaster,
  reconcileInk,
  assertReconciled,
  inkIssues,
  type DrawingDocument,
  type DrawingEntity,
  type GreyscaleImage,
  type MergeResult,
  type QualityIssue,
  type RasterLedger,
  type ScaleSource,
  type DxfTextStyle,
  type Artefacts,
  type Metrics,
  type ErrorCode,
} from '@drawing2d/core';

/**
 * The conversion pipeline, run entirely in the browser.
 *
 * Nothing here uploads anything. A drawing dropped on this page is read by the
 * tab it was dropped on and never leaves the machine — which is the design
 * answer to the rule that customer drawings may not appear on a server, and the
 * reason an engineer can try a confidential drawing without asking anyone.
 */

let configured = false;

/**
 * pdf.js needs a worker and its character maps. The engine deliberately makes
 * no assumption about where either lives, so the host supplies them.
 */
async function configurePdfJs(): Promise<void> {
  if (configured) return;
  const pdfjs = await loadPdfJs();
  const workerUrl = new URL('pdfjs-dist/legacy/build/pdf.worker.mjs', import.meta.url).href;
  (pdfjs.GlobalWorkerOptions as { workerSrc: string }).workerSrc = workerUrl;
  configured = true;
}

function assetBase(): string {
  // `base` is relative, so asset URLs are resolved against the document.
  return new URL('pdfjs/', document.baseURI).href;
}

/**
 * Where the site's own static files live.
 *
 * Separate from `assetBase`, which is pdf.js's directory and not the site's —
 * a distinction the name does not make, and which cost a font fetched from
 * `pdfjs/fonts/` where nothing is served. The build failed silently: the request
 * 404s, the outlining throws, and the conversion reports a failure that says
 * nothing about fonts.
 */
function siteBase(): string {
  return document.baseURI;
}

export interface RunResult {
  document: DrawingDocument;
  artefacts: Artefacts;
  metrics: Metrics;
  /** Page bitmaps, rendered once, for the overlay backdrop. */
  pageImages: string[];
  /**
   * What the outlining did, or null when it was not asked for.
   *
   * Carried out of the pipeline rather than kept inside it because the half that
   * matters to a reader is `missing` — the characters no glyph was available for.
   * The subset covers the whole of JIS X 0208, which is a bounded claim and not
   * a complete one, and a bounded claim has to be visible to be honest.
   */
  outlines: DocumentOutlines | null;
  /**
   * What is wrong with the drawing that was uploaded.
   *
   * Carried on the result rather than fetched separately, because the whole
   * point is that it is seen: a conversion that scored well on a drawing whose
   * text was written as outlines is a conversion of a different drawing, and the
   * reader has to be told before they trust the output.
   */
  validation: ValidationReport;
  /**
   * Whether every entity in `drawing.json` is in the DXF, as itself.
   *
   * Null when no DXF was written — scale unresolved, so there is nothing to
   * check. Otherwise the operator is looking at a file they are about to
   * download, and "every entity you are taking away is the one the drawing
   * declared" is a different claim from an accuracy figure and a more useful one
   * at that moment.
   */
  identity: IdentityReport | null;
  elapsedMs: number;
}

export interface RunFailure {
  code: ErrorCode | 'UNEXPECTED';
  message: string;
  /**
   * Null for a failure that is not the file's fault.
   *
   * An unexpected internal error gets no re-export advice, because telling
   * somebody to change their export settings over a defect in this software
   * sends them to fix the wrong thing.
   */
  validation: ValidationReport | null;
}

/**
 * Fetches the bundled Japanese font and turns it into a glyph source.
 *
 * Fetched here rather than bundled, and only when asked for: it is 1.5 MB, which
 * is an order of magnitude more than the rest of this application, and every
 * operator who does not need outlines should never pay for it. `opentype.js`
 * comes with it, behind the same dynamic import.
 *
 * Cached for the session, because switching the option off and on again should
 * not re-download it.
 */
let glyphSourcePromise: Promise<GlyphSource> | null = null;

async function japaneseGlyphs(): Promise<GlyphSource> {
  glyphSourcePromise ??= (async () => {
    const response = await fetch(new URL('fonts/NotoSansJP-jis.otf', siteBase()).href);
    if (!response.ok) {
      throw new Error(`the Japanese font could not be loaded (${String(response.status)})`);
    }
    return glyphSourceFromFont(await response.arrayBuffer(), 'NotoSansJP-jis');
  })();
  return glyphSourcePromise;
}

export async function runPipeline(
  bytes: Uint8Array,
  scale: ScaleSource | null,
  textStyle?: DxfTextStyle,
  outlineText = false,
): Promise<RunResult> {
  await configurePdfJs();
  const started = performance.now();

  const extracted = await extractDocument(bytes, {
    cMapUrl: `${assetBase()}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${assetBase()}standard_fonts/`,
  });

  const document_ = scale ? resolveScale(extracted, scale) : extracted;

  // Only when asked for. The font download and the outlining both hang off this
  // branch, so an operator who leaves the option alone runs exactly the pipeline
  // that ran before it existed.
  let outlines: DocumentOutlines | null = null;
  if (outlineText) {
    outlines = outlineDocument(document_, await japaneseGlyphs());
  }

  const artefacts = await buildArtefacts(document_, {
    ...(textStyle ? { textStyle } : {}),
    ...(outlines ? { textOutlines: outlines.byPage } : {}),
  });
  const metrics = computeMetrics(document_);
  const pageImages = await renderPages(bytes);

  return {
    document: document_,
    artefacts,
    metrics,
    pageImages,
    outlines,
    // Validated on the extracted document, before the DXF is offered for
    // download: extraction is the read, so this is as early as the answer
    // exists, and it is still before anyone acts on the output.
    validation: validateDocument(document_),
    identity: checkIdentity(document_, artefacts.dxf),
    elapsedMs: Math.round(performance.now() - started),
  };
}

/**
 * The DXF that was just written, read back and compared entity by entity.
 *
 * The same check the acceptance runner makes, on the drawing in front of the
 * person rather than on a corpus — which is the half that matters here, because
 * a corpus figure says nothing about the file they are about to download.
 */
function checkIdentity(doc: DrawingDocument, dxf: string | null): IdentityReport | null {
  if (dxf === null) return null;
  const declared = doc.pages.flatMap((p) => p.entities.map((e) => toDrawingSpace(e, p.transform)));
  return verifyIdentity(declared, readDxf(dxf).entities, {
    toleranceMm: ROUND_TRIP_TOLERANCE_MM,
    arcAngleToleranceDeg: ROUND_TRIP_ANGLE_TOLERANCE_DEG,
    pages: doc.pages.map((p) => p.entities),
  });
}

export function describeFailure(err: unknown): RunFailure {
  if (err instanceof ExtractionError) {
    return { code: err.code, message: err.message, validation: validateFailure(err) };
  }
  return {
    code: 'UNEXPECTED',
    message: err instanceof Error ? err.message : String(err),
    validation: null,
  };
}

/**
 * Renders each page to a data URL for the overlay backdrop.
 *
 * The overlay is drawn from the *structured output*, not from this image. The
 * image is there so a person can see whether the two agree — which is the whole
 * point of an overlay, and the only way to check a conversion by eye.
 */
async function renderPages(bytes: Uint8Array): Promise<string[]> {
  const pdfjs = await loadPdfJs();
  const pdf = await pdfjs.getDocument({
    data: bytes.slice(),
    isEvalSupported: false,
    disableFontFace: true,
    cMapUrl: `${assetBase()}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${assetBase()}standard_fonts/`,
  }).promise;

  const out: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) continue;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    // The canvas is passed alongside its context because pdf.js needs both at
    // runtime and throws without it, while the types shipped with this version
    // still describe the older signature. Written as a widened parameter type
    // rather than silenced, so that when the types catch up this line stops
    // compiling and gets removed rather than lingering as a mystery.
    type RenderWithCanvas = Parameters<typeof page.render>[0] & { canvas: HTMLCanvasElement };
    const parameters: RenderWithCanvas = { canvasContext: context, viewport, canvas };
    await page.render(parameters).promise;
    out.push(canvas.toDataURL('image/png'));
  }
  return out;
}

/** The board fixes these three, and the surface shows which one was used. */
export const RASTER_DPI_CHOICES = [300, 450, 600] as const;

export interface RasterRunResult {
  dpi: number;
  widthPx: number;
  heightPx: number;
  pixelsPerPoint: number;
  pageWidthPt: number;
  pageHeightPt: number;
  /** The skew found, in degrees. Reported, not silently corrected — see below. */
  skewDegrees: number;
  inkFraction: number;
  rasterEntities: DrawingEntity[];
  vectorEntityCount: number;
  merge: MergeResult;
  /**
   * What the scan contained and how much of it a detection named.
   *
   * Carried here rather than computed only by the acceptance runner, because a
   * claim that appears only in a report is not the product's claim — the same
   * reason the operator ledger is on the conversion surface and not in a file.
   */
  ledger: RasterLedger;
  /**
   * The ledger's located regions, as quality issues — kept **apart** from the
   * merge's, and that separation is the point.
   *
   * These were briefly folded into one list with the merge's findings under
   * "Flagged for review", and on a sheet where the raster gate passes at F1
   * 1.000 that list read 249: one genuine low-confidence detection and 248 text
   * glyphs and arcs the detector never attempted. A review queue that is
   * mostly accounting is a review queue nobody reads.
   *
   * `merge` is left exactly as `mergeRaster` returned it, for the same reason
   * in the other direction: its counts must not describe findings it never made.
   */
  inkIssues: QualityIssue[];
  pageImage: string | null;
  elapsedMs: number;
}

/**
 * Renders a page to greyscale for the raster pipeline.
 *
 * This is the ONLY place a canvas appears. `packages/core` takes a plain
 * `GreyscaleImage` precisely so the engine has no DOM dependency and the same
 * preprocessing runs in Node tests and against the committed .pgm fixtures.
 */
export async function rasterise(bytes: Uint8Array, dpi: number): Promise<GreyscaleImage> {
  await configurePdfJs();
  const pdfjs = await loadPdfJs();
  const pdf = await pdfjs.getDocument({
    data: bytes.slice(),
    isEvalSupported: false,
    disableFontFace: true,
    cMapUrl: `${assetBase()}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${assetBase()}standard_fonts/`,
  }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: dpi / 72 });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('a 2D canvas context is required to read a scan');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  // Same widened parameter type as `renderPages`, and for the same reason.
  type RenderWithCanvas = Parameters<typeof page.render>[0] & { canvas: HTMLCanvasElement };
  const parameters: RenderWithCanvas = { canvasContext: context, viewport, canvas };
  await page.render(parameters).promise;

  const rgba = context.getImageData(0, 0, canvas.width, canvas.height).data;
  const pixels = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0; i < pixels.length; i += 1) {
    // Rec. 601 luma. A plain mean shifts a coloured revision cloud's weight.
    pixels[i] = Math.round(
      0.299 * rgba[i * 4]! + 0.587 * rgba[i * 4 + 1]! + 0.114 * rgba[i * 4 + 2]!,
    );
  }
  return { width: canvas.width, height: canvas.height, pixels };
}

/**
 * A scan, read into the same entity set the vector pipeline produces.
 *
 * Two decisions here are worth knowing about.
 *
 * **The skew is measured and not corrected.** Correcting it would rotate the
 * geometry into a frame the page bitmap behind it is not in, so the overlay would
 * no longer sit on the drawing it is drawn over — and the acceptance runner scores
 * in this same uncorrected frame, so the surface and the measurement agree about
 * what was produced. The angle is reported instead, which is what an operator
 * looking at a bad scan actually wants to know.
 *
 * **The vector layer is extracted too, and wins every conflict.** A hybrid sheet is
 * part geometry and part pixels; `mergeRaster` keeps the exact where it exists and
 * the estimate only where it does not. If extraction finds nothing — an image-only
 * PDF — the merge runs against an empty vector layer and the result is all raster,
 * every entity carrying its confidence.
 */
export async function runRasterPipeline(bytes: Uint8Array, dpi: number): Promise<RasterRunResult> {
  await configurePdfJs();
  const started = performance.now();

  const image = await rasterise(bytes, dpi);
  const pixelsPerPoint = dpi / 72;
  const pageWidthPt = image.width / pixelsPerPoint;
  const pageHeightPt = image.height / pixelsPerPoint;

  const pre = preprocess(image, { deskew: false });
  const context = { pixelsPerPoint, pageHeightPt };

  // The detections are held in a variable rather than passed straight into
  // `toEntities`, because the ledger has to account against the very list that
  // was reported. Detecting a second time for the ledger would let the two
  // disagree, and the account would then be of a run nobody sees.
  const detections = baselineDetector.detect(pre.binary, context);
  const rasterEntities = toEntities(detections, context);

  // The ink account, asserted here in the product path. A run that does not
  // reconcile is not shown with a caveat — it throws, because the figure on the
  // stamp is the one claim on this surface that has to be exact.
  const ledger = reconcileInk(pre.binary, detections, context);
  assertReconciled(ledger);

  // A scan may carry a vector layer as well; the hybrid fixture does. Extraction
  // failing is not a reason to refuse the scan, so an empty vector layer stands in.
  let vectorEntities: DrawingEntity[] = [];
  try {
    const extracted = await extractDocument(bytes, {
      cMapUrl: `${assetBase()}cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `${assetBase()}standard_fonts/`,
    });
    vectorEntities = extracted.pages.flatMap((page) => page.entities);
  } catch {
    vectorEntities = [];
  }

  const merge = mergeRaster(vectorEntities, rasterEntities);
  const pageImages = await renderPages(bytes);

  return {
    dpi,
    widthPx: image.width,
    heightPx: image.height,
    pixelsPerPoint,
    pageWidthPt,
    pageHeightPt,
    skewDegrees: pre.skewDegrees,
    inkFraction: pre.inkFraction,
    rasterEntities,
    vectorEntityCount: vectorEntities.length,
    merge,
    ledger,
    inkIssues: inkIssues(ledger, context),
    pageImage: pageImages[0] ?? null,
    elapsedMs: Math.round(performance.now() - started),
  };
}

export function downloadText(name: string, text: string, type: string): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
